/**
 * MaxPainCharts — three charts for the (3/5) Max Pain section:
 *   1. Call vs Put OI Distribution (grouped bar)
 *   2. Pain Curve with global min (max pain) + pinning points
 *   3. Change in OI (ΔOI) — Call bars positive, Put bars inverted
 *
 * Props:
 *   data   — API response from /api/max-pain-chain/<symbol>/
 *   isDark — boolean for theme
 */

import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Legend,
} from 'recharts';

const C = {
  call:      'rgba(239,68,68,0.80)',
  put:       'rgba(34,197,94,0.80)',
  maxPain:   '#f59e0b',
  spot:      '#3b82f6',
  painLine:  '#8b5cf6',
  pinBelow:  '#ec4899',
  pinAbove:  '#06b6d4',
};

/** Find the strike in `chain` closest to `value`. Used to snap reference lines. */
function snapStrike(chain, value) {
  if (value == null || !chain?.length) return null;
  return chain.reduce((a, b) =>
    Math.abs(a.strike - value) < Math.abs(b.strike - value) ? a : b
  ).strike;
}

function useChartTheme(isDark) {
  return {
    textColor: isDark ? '#c8d8e0' : '#5f5f5f',
    gridColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.06)',
    tooltipBg: isDark ? '#122833' : '#fff',
    tooltipBorder: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
    zeroLine: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.15)',
  };
}

/** Shared X-axis tick config for strike prices. */
function xTickProps(textColor) {
  return {
    tick: { fontSize: 8.5, fill: textColor },
    tickFormatter: (v) => Number(v).toLocaleString('en-IN', { maximumFractionDigits: 0 }),
    interval: 'preserveStartEnd',
    angle: -40,
    textAnchor: 'end',
    height: 38,
  };
}

/** Shared Y-axis tick config. */
function yTickProps(textColor, formatter, width = 52) {
  return {
    tick: { fontSize: 8.5, fill: textColor },
    tickFormatter: formatter,
    width,
  };
}

/** Shared tooltip style. */
function tooltipContentStyle(tooltipBg, tooltipBorder, textColor) {
  return {
    background: tooltipBg,
    border: `1px solid ${tooltipBorder}`,
    borderRadius: 6,
    fontSize: 11,
    color: textColor,
  };
}

export default function MaxPainCharts({ data, isDark }) {
  if (!data?.chain?.length) return null;

  const theme = useChartTheme(isDark);
  const { chain, spot_price, max_pain, below_pins, above_pins, is_index } = data;

  // Scale OI values for display
  const oi_scale     = is_index ? 1e5 : 1;
  const oi_scale_lbl = is_index ? 'Lakh ctrcts' : 'contracts';

  // Scale pain values for display
  const maxRawPain  = Math.max(...chain.map((d) => d.total_pain));
  const pain_scale  = maxRawPain > 1e10 ? 1e10 : maxRawPain > 1e8 ? 1e8 : 1e6;
  const pain_lbl    = maxRawPain > 1e10 ? '×10¹⁰' : maxRawPain > 1e8 ? '×10⁸' : '×10⁶';

  // Snap reference positions to actual strikes (categorical X axis)
  const spotStrike = snapStrike(chain, spot_price);
  const mpStrike   = max_pain; // already a strike value
  const belowSnapped = below_pins.map((s) => snapStrike(chain, s)).filter(Boolean);
  const aboveSnapped = above_pins.map((s) => snapStrike(chain, s)).filter(Boolean);

  // Data for ΔOI chart: negate PE so it appears below zero
  const deltaData = chain.map((d) => ({
    ...d,
    pe_chg_oi_inv: -d.pe_chg_oi,
  }));

  const fmtOI    = (v) => (v / oi_scale).toFixed(1);
  const fmtPain  = (v) => (v / pain_scale).toFixed(1);
  const fmtPrice = (v) => Number(v).toLocaleString('en-IN', { maximumFractionDigits: 0 });

  const commonMargin = { top: 8, right: 10, bottom: 38, left: 6 };

  // ── Shared reference line definitions ──────────────────────────────────────
  const spotLine = spotStrike != null ? (
    <ReferenceLine
      x={spotStrike}
      stroke={C.spot}
      strokeWidth={2}
      strokeDasharray="5 3"
      label={{ value: `Spot ₹${fmtPrice(spot_price)}`, position: 'insideTopLeft', fill: C.spot, fontSize: 8.5, offset: 4 }}
    />
  ) : null;

  const mpLine = mpStrike != null ? (
    <ReferenceLine
      x={mpStrike}
      stroke={C.maxPain}
      strokeWidth={2}
      strokeDasharray="5 3"
      label={{ value: `MP ₹${fmtPrice(max_pain)}`, position: 'insideTopRight', fill: C.maxPain, fontSize: 8.5, offset: 4 }}
    />
  ) : null;

  const belowLines = belowSnapped.map((s) => (
    <ReferenceLine key={`bp-${s}`} x={s} stroke={C.pinBelow} strokeWidth={1.5} strokeDasharray="3 2" />
  ));
  const aboveLines = aboveSnapped.map((s) => (
    <ReferenceLine key={`ap-${s}`} x={s} stroke={C.pinAbove} strokeWidth={1.5} strokeDasharray="3 2" />
  ));

  return (
    <div className="flex flex-col gap-6">

      {/* ── Chart 1: OI Distribution ──────────────────────────────────────────── */}
      <div>
        <p className="text-xs font-bold uppercase tracking-widest text-neutral-400 dark:text-neutral-500 mb-2">
          Call vs Put Open Interest by Strike
        </p>
        <ResponsiveContainer width="100%" height={240}>
          <ComposedChart data={chain} margin={commonMargin} barCategoryGap="20%">
            <CartesianGrid stroke={theme.gridColor} />
            <XAxis dataKey="strike" {...xTickProps(theme.textColor)} />
            <YAxis
              {...yTickProps(theme.textColor, fmtOI)}
              label={{ value: oi_scale_lbl, angle: -90, position: 'insideLeft', fontSize: 8.5, fill: theme.textColor, offset: 4 }}
            />
            <Tooltip
              contentStyle={tooltipContentStyle(theme.tooltipBg, theme.tooltipBorder, theme.textColor)}
              formatter={(v, name) => [`${fmtOI(v)} ${oi_scale_lbl}`, name]}
              labelFormatter={(l) => `Strike ₹${fmtPrice(l)}`}
            />
            <Legend
              wrapperStyle={{ fontSize: 10, color: theme.textColor, paddingTop: 2 }}
              iconSize={10}
            />

            <Bar dataKey="ce_oi" name="Call OI" fill={C.call} isAnimationActive={false} />
            <Bar dataKey="pe_oi" name="Put OI"  fill={C.put}  isAnimationActive={false} />

            {spotLine}
            {mpLine}
            {belowLines}
            {aboveLines}
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* ── Charts 2 & 3 side by side ─────────────────────────────────────────── */}
      <div className="grid grid-cols-2 gap-4">

        {/* Chart 2: Pain Curve */}
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-neutral-400 dark:text-neutral-500 mb-2">
            Pain Curve — Global min = Max Pain · Local mins = Pinning Points
          </p>
          <ResponsiveContainer width="100%" height={230}>
            <ComposedChart data={chain} margin={commonMargin}>
              <CartesianGrid stroke={theme.gridColor} />
              <XAxis dataKey="strike" {...xTickProps(theme.textColor)} />
              <YAxis
                {...yTickProps(theme.textColor, fmtPain)}
                label={{ value: `Pain (${pain_lbl})`, angle: -90, position: 'insideLeft', fontSize: 8.5, fill: theme.textColor, offset: 4 }}
              />
              <Tooltip
                contentStyle={tooltipContentStyle(theme.tooltipBg, theme.tooltipBorder, theme.textColor)}
                formatter={(v) => [`${fmtPain(v)} ${pain_lbl}`, 'Pain']}
                labelFormatter={(l) => `Strike ₹${fmtPrice(l)}`}
              />

              <Line
                dataKey="total_pain"
                stroke={C.painLine}
                strokeWidth={2}
                dot={false}
                name="Pain"
                isAnimationActive={false}
              />

              {spotLine}
              {/* Max pain reference with star label */}
              {mpStrike != null && (
                <ReferenceLine
                  x={mpStrike}
                  stroke={C.maxPain}
                  strokeWidth={2.5}
                  strokeDasharray="5 3"
                  label={{ value: `★ ₹${fmtPrice(max_pain)}`, position: 'insideTopRight', fill: C.maxPain, fontSize: 8.5 }}
                />
              )}
              {belowSnapped.map((s) => (
                <ReferenceLine key={`bp2-${s}`} x={s} stroke={C.pinBelow} strokeWidth={1.5} strokeDasharray="3 2"
                  label={{ value: `↓₹${fmtPrice(s)}`, position: 'top', fill: C.pinBelow, fontSize: 8 }} />
              ))}
              {aboveSnapped.map((s) => (
                <ReferenceLine key={`ap2-${s}`} x={s} stroke={C.pinAbove} strokeWidth={1.5} strokeDasharray="3 2"
                  label={{ value: `↑₹${fmtPrice(s)}`, position: 'top', fill: C.pinAbove, fontSize: 8 }} />
              ))}
            </ComposedChart>
          </ResponsiveContainer>
        </div>

        {/* Chart 3: ΔOI */}
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-neutral-400 dark:text-neutral-500 mb-2">
            Change in OI (ΔOI) — Put inverted · Pin lines carried over
          </p>
          <ResponsiveContainer width="100%" height={230}>
            <ComposedChart data={deltaData} margin={commonMargin} barCategoryGap="20%">
              <CartesianGrid stroke={theme.gridColor} />
              <XAxis dataKey="strike" {...xTickProps(theme.textColor)} />
              <YAxis
                {...yTickProps(theme.textColor, fmtOI)}
                label={{ value: oi_scale_lbl, angle: -90, position: 'insideLeft', fontSize: 8.5, fill: theme.textColor, offset: 4 }}
              />
              <Tooltip
                contentStyle={tooltipContentStyle(theme.tooltipBg, theme.tooltipBorder, theme.textColor)}
                formatter={(v, name) => [`${fmtOI(Math.abs(v))} ${oi_scale_lbl}`, name]}
                labelFormatter={(l) => `Strike ₹${fmtPrice(l)}`}
              />
              <Legend
                wrapperStyle={{ fontSize: 10, color: theme.textColor, paddingTop: 2 }}
                iconSize={10}
              />

              <Bar dataKey="ce_chg_oi"   name="Call ΔOI" fill={C.call} isAnimationActive={false} />
              <Bar dataKey="pe_chg_oi_inv" name="Put ΔOI"  fill={C.put}  isAnimationActive={false} />

              {/* Zero baseline */}
              <ReferenceLine y={0} stroke={theme.zeroLine} strokeWidth={1} />

              {spotLine}
              {mpLine}
              {belowLines}
              {aboveLines}
            </ComposedChart>
          </ResponsiveContainer>
        </div>

      </div>
    </div>
  );
}
